import moment from 'moment';
import React from 'react';
import { Link } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components';
import { getCvsApplications } from '~/javascripts/admin/actions/cvs';
import { formatDate, formatNum, formatUUID } from '~/javascripts/admin/utils';
import { createBadge } from '../../atoms/Badge';
import Button from '../../atoms/Buttons';
import Table from '../../atoms/Table';
import SeqPager from '../../organisms/SeqPager';

type Props = {
  cvsApplications?: Awaited<ReturnType<typeof getCvsApplications>>;
  onChangePage: (paginatedLink: string | null) => void;
  onCancelCvs: (orderId: string) => void;
};

type Header = {
  label: string;
  size: number;
  align?: 'center' | 'justify' | 'left' | 'right';
  tooltipKey?: string;
  tooltipText?: string | React.ReactNode;
};

const headers: Header[] = [
  { label: '状態', size: 1 },
  { label: '受付番号', size: 1 },
  { label: '支払期限', size: 1 },
  {
    label: '支払日時',
    size: 1,
    tooltipKey: 'paymentDate',
    tooltipText: 'お客様がコンビニで支払を完了した日時',
  },
  { label: '取引ID', size: 1 },
  { label: '金額', size: 1, align: 'right' },
  { label: 'お客様ID', size: 1 },
  { label: '姓名', size: 2 },
  { label: '操作', size: 3 },
];

const TooltipTableCell = styled(Table.Cell)<{ tooltip?: boolean }>`
  &:hover {
    ${({ tooltip }) => tooltip && 'cursor: pointer; background-color: #eeeeee;'}
  }
`;

const CvsStatusBadge = createBadge({
  applied: { label: '申込完了', bgColor: '#3C86C9' },
  payed: { label: '支払完了', bgColor: '#3C86C9' },
  canceled: { label: '取消', bgColor: '#585F63' },
  expired: { label: '期限切れ', bgColor: '#585F63' },
});

const getStatus = (cvs: Awaited<ReturnType<typeof getCvsApplications>>['items'][number]) => {
  if (cvs.done_at) return 'payed';
  if (cvs.canceled_at) return 'canceled';
  if (moment(cvs.pay_limit).isAfter(moment())) return 'applied';
  return 'expired';
};

const ListTable = ({ cvsApplications, onChangePage, onCancelCvs }: Props) => {
  return (
    <Table>
      <Table.Header>
        <Table.Row>
          {headers.map((c) => (
            <TooltipTableCell
              size={c.size}
              key={c.label}
              align={c.align}
              tooltip={!!c.tooltipKey && !!c.tooltipText}
              data-tip
              data-for={c.tooltipKey}
            >
              {c.label}
              {!!c.tooltipKey && !!c.tooltipText && (
                <ReactTooltip id={c.tooltipKey} type="dark" place="top" effect="solid">
                  <span>{c.tooltipText}</span>
                </ReactTooltip>
              )}
            </TooltipTableCell>
          ))}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {cvsApplications?.items?.map((cvs) => (
          <Table.Row key={cvs.order_id}>
            <Table.Cell wordBreak="keep-all">
              <CvsStatusBadge status={getStatus(cvs)} />
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">{cvs.receipt_no}</Table.Cell>
            <Table.Cell wordBreak="keep-all">{formatDate(cvs.pay_limit)}</Table.Cell>
            <Table.Cell wordBreak="keep-all">
              {cvs.done_at ? formatDate(cvs.done_at) : ''}
            </Table.Cell>
            <Table.Cell wordBreak="keep-all">
              {cvs.transaction_id ? (
                <Link to={`/transactions/${cvs.transaction_id}`}>
                  {formatUUID(cvs.transaction_id)}
                </Link>
              ) : (
                ''
              )}
            </Table.Cell>
            <Table.Cell wordBreak="keep-all" align="right">
              {formatNum(cvs.amount)}
            </Table.Cell>
            <Table.Cell wordBreak="break-word">
              <Link to={`/customers/${cvs.user.id}`}>{formatUUID(cvs.user.id)}</Link>
            </Table.Cell>
            <Table.Cell wordBreak="break-word">
              {cvs.name1} {cvs.name2}
            </Table.Cell>
            <Table.Cell>
              {getStatus(cvs) === 'applied' && (
                <Button onClick={() => onCancelCvs(cvs.order_id)} variant="danger">
                  申込取消
                </Button>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
      <Table.Footer>
        <Table.Row noBorder>
          <Table.Cell colSpan={headers.length} align="center" styles={{ paddingTop: '16px' }}>
            {cvsApplications && (
              <SeqPager
                prev={cvsApplications.prev}
                next={cvsApplications.next}
                onChangePage={onChangePage}
              />
            )}
          </Table.Cell>
        </Table.Row>
      </Table.Footer>
    </Table>
  );
};

export default ListTable;
